import React, { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../../common/SafeIcon'
import { supabase } from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'

const { FiMail, FiLock, FiEye, FiEyeOff, FiUser } = FiIcons

const AuthForm = () => {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      console.log('⏰ Auth timeout - checking if user is actually logged in...')
      
      // Check if user got signed in despite hanging promise
      const checkAuth = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) {
            console.log('✅ User is signed in despite timeout, navigating...')
            setLoading(false)
            navigate('/dashboard')
            return
          }
        } catch (error) {
          console.error('Failed to check session:', error)
        }
        
        setLoading(false)
        setError('Login timed out. Please try again.')
      }
      
      checkAuth()
    }, 6000) // 6 second timeout

    try {
      console.log('🔐 Attempting', isSignUp ? 'sign up' : 'sign in', 'with:', email)
      
      // Validate terms for signup
      if (isSignUp && !acceptedTerms) {
        setError('You must accept the Terms of Service and Privacy Policy to create an account.')
        setLoading(false)
        clearTimeout(timeoutId)
        return
      }
      
      let result
      if (isSignUp) {
        result = await signUp(email, password, { 
          displayName,
          acceptedTerms: true // Include this in metadata
        })
      } else {
        result = await signIn(email, password)
      }

      clearTimeout(timeoutId) // Clear timeout if auth completes
      console.log('📡 Auth result:', { success: !result.error, error: result.error?.message })

      if (result.error) {
        console.error('❌ Auth error:', result.error)
        setError(result.error.message)
      } else {
        console.log('✅ Auth successful!')
        if (isSignUp) {
          setError('')
          alert('Account created successfully! You can now sign in.')
          setIsSignUp(false) // Switch to sign in
        } else {
          console.log('🎯 Navigating to dashboard...')
          navigate('/dashboard')
        }
      }
    } catch (err) {
      clearTimeout(timeoutId)
      console.error('💥 Unexpected auth error:', err)
      setError('An unexpected error occurred: ' + err.message)
    } finally {
      console.log('🏁 Setting loading = false')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center mx-auto mb-4 p-2">
              <img 
                src="https://ymwrzyarkxxnsqllnnfu.supabase.co/storage/v1/object/public/assets/nupitch_logo_ol-Centered_001.svg" 
                alt="NuPitch Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome to NuPitch</h1>
            <p className="text-gray-600 mt-2">
              {isSignUp ? 'Create your account to start building film pitch decks' : 'Sign in to continue creating amazing pitch decks'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your name"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
              {isSignUp && (
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              )}
            </div>

            {/* Terms Acceptance Checkbox (for sign up only) */}
            {isSignUp && (
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  required
                />
                <label htmlFor="accept-terms" className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a
                    href="/#/terms"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="/#/privacy"
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (isSignUp && !acceptedTerms)}
              className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp)
                setError('')
                setAcceptedTerms(false) // Reset terms checkbox when switching
              }}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {isSignUp 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Sign up"
              }
            </button>
          </div>

          {/* Legal Links */}
          <div className="mt-6 text-center space-x-4">
            <a
              href="/#/terms"
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Terms of Service
            </a>
            <span className="text-gray-300">•</span>
            <a
              href="/#/privacy"
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthForm